import { daysOfTheWeekP, typesOfPreaching } from '@monorepo/helpers';
import { FaExclamationCircle } from 'react-icons/fa';
import { FC, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { typePublicMinistryMonthlyService, typePublicMinistryService, typePublicMinistryTypeOfPreaching, typeUser } from '@monorepo/models';
import { useGetConfig, useGetIsDarkMode } from 'src/custom-hooks';
import Select from 'react-select';

type propsType = {
    addService?: (service: typePublicMinistryService) => void;
    closeModal: () => void;
    deleteService?: (service: typePublicMinistryService) => void;
    editService?: (service: typePublicMinistryService) => void;
    monthlyService: typePublicMinistryMonthlyService;
    serviceToEdit?: typePublicMinistryService;
    users: typeUser[];
};

export const PublicMinistryCrudServiceModal: FC<propsType> = ({
    addService, closeModal, deleteService, editService, monthlyService, serviceToEdit, users
}) => {
    const getInit = (): typePublicMinistryService => ({
        address1: '',
        address2: '',
        captain1: null,
        captain2: null,
        day: null,
        groups: [],
        hour: 9,
        id: 0,
        isCircuitOverseer: false,
        minutes: 0,
        observation: '',
        territories: [],
        typeOfPreaching: 'house-to-house',
        typeOfRegularity: 1,
        weekday: null
    });
    //
    const [disabledAddOtherBtn, setDisabledAddOtherBtn] = useState(false);
    const [service, setService] = useState<typePublicMinistryService>(serviceToEdit ?? getInit());
    const config = useGetConfig();
    const isDarkMode = useGetIsDarkMode();
    const isEdit = !!serviceToEdit;
    //
    const lastDayOfTheMonth = new Date(monthlyService.year, monthlyService.month + 1, 0).getDate();
    const maxDate = `${monthlyService.year}-${String(monthlyService.month + 1).padStart(2, '0')}-${String(lastDayOfTheMonth).padStart(2, '0')}`;
    const minDate = `${monthlyService.year}-${String(monthlyService.month + 1).padStart(2, '0')}-01`;

    const isThereOverlap: boolean = useMemo(() =>
        !monthlyService
    , [monthlyService]);

    const isValid: boolean = useMemo(() =>
        (
            (service.typeOfRegularity === 1 && !service.day && !service.groups.length && !service.isCircuitOverseer && service.weekday !== null)
            || (service.typeOfRegularity === 2 && !service.day && !!service.groups.length && !service.isCircuitOverseer && service.weekday !== null)
            || (service.typeOfRegularity === 3 && !!service.day && !service.groups.length && !service.isCircuitOverseer && service.weekday === null)
            || (service.typeOfRegularity === 4 && !!service.day && !!service.groups.length && !service.isCircuitOverseer && service.weekday === null)
            || (service.typeOfRegularity === 5 && !!service.day && !service.groups.length && service.isCircuitOverseer && service.weekday === null)
        )
        && !isThereOverlap
        && (!service.address2 || !!service.address1)
    , [isThereOverlap, service]);

    const repeatedTerritories: Array<number> = useMemo(() => {
        return [
            ...new Set(
                service.territories.filter(t => 
                    monthlyService.services.flatMap(s => s.territories).includes(t)
                )
            )
        ].sort((t1, t2) => t1 - t2);
    }, [monthlyService, service]);

    const acceptHandler = (closing: boolean) => {
        if (!isValid) {
            return;
        }
        if (isEdit) {
            editService?.(service);
        } else {
            addService?.(service);
        }
        if (closing) {
            closeModal();
        } else {
            setService(x => ({ ...x, id: 0 }));
            setDisabledAddOtherBtn(true);
        }
    }

    useEffect(() => {
        setTimeout(() => {
            setDisabledAddOtherBtn(false);
        }, 4000);
    }, [disabledAddOtherBtn]);

    return (
        <Modal show
            fullscreen={'xl-down'}
            onHide={closeModal}
            size={'xl'}
        >
            <Modal.Header className={isDarkMode ? 'bg-dark text-white border-white' : ''} closeButton>
                <h3 className={'mb-0'}>
                    AGREGAR
                </h3>
            </Modal.Header>
            <Modal.Body className={isDarkMode ? 'bg-dark border-white' : ''}>
                <div className={`card ${isDarkMode ? 'bg-lightdark text-white border-white' : ''}`}>
                    <div className={'card-body row'}>
                        <div className={'col-lg-3'}>
                            <h5>
                                Tipo de Salida
                            </h5>
                            <select className={'form-select'}
                                value={service.typeOfRegularity}
                                onChange={e => {
                                    const v = parseInt(e.target.value) || 0;
                                    if (v === 1) {
                                        setService(x => ({ ...x, typeOfRegularity: v, day: null, groups: [], isCircuitOverseer: false, weekday: null }));
                                    } else if (v === 2) {
                                        setService(x => ({ ...x, typeOfRegularity: v, day: null, groups: [], isCircuitOverseer: false, weekday: null }));
                                    } else if (v === 3) {
                                        setService(x => ({ ...x, typeOfRegularity: v, day: null, groups: [], isCircuitOverseer: false, weekday: null }));
                                    } else if (v === 4) {
                                        setService(x => ({ ...x, typeOfRegularity: v, day: null, groups: [], isCircuitOverseer: false, weekday: null }));
                                    } else if (v === 5) {
                                        setService(x => ({ ...x, typeOfRegularity: v, day: null, groups: [], isCircuitOverseer: true, weekday: null }));
                                    }
                                }}
                            >
                                <option value={1}>
                                    Día de la Semana
                                </option>
                                <option value={2}>
                                    Día de la Semana de Grupos
                                </option>
                                <option value={3}>
                                    Día Específico
                                </option>
                                <option value={4}>
                                    Día Específico de Grupos
                                </option>
                                <option value={5}>
                                    Día Específico de la Visita
                                </option>
                            </select>
                            {[1, 2].includes(service.typeOfRegularity) ?
                                <>
                                <h5 className={'mt-3'}>
                                    Día de la Semana
                                </h5>
                                <select className={'form-select'}
                                    value={service.weekday ?? ''}
                                    onChange={e => {
                                        const v = parseInt(e.target.value);
                                        setService(x => ({ ...x, weekday: v }))
                                    }}
                                >
                                    <option value='' disabled>
                                        Seleccionar Día de la Semana
                                    </option>
                                    {[...daysOfTheWeekP.slice(1), daysOfTheWeekP[0]].map((d, i) =>
                                        <option key={i} value={i === 6 ? 0 : i + 1}>
                                            {d}
                                        </option>
                                    )}
                                </select>
                                </>
                                :
                                <>
                                <h5 className={'mt-3'}>
                                    Fecha
                                </h5>
                                <input type={'date'}
                                    className={'form-control'}
                                    value={service.day ?
                                        new Date(monthlyService.year, monthlyService.month, service.day).toISOString().split('T')[0]
                                        :
                                        ''
                                    }
                                    onChange={e => {
                                        const v = parseInt(e.target.value.split('-')[2]) || 0;
                                        setService(x => ({ ...x, day: v }));
                                    }}
                                    min={minDate}
                                    max={maxDate}
                                />
                                </>
                            }
                            <h5 className={'mt-3'}>
                                Horario
                            </h5>
                            <div className={'d-flex justify-content-start'}>
                                <Select className={'text-dark me-2'}
                                    options={Array.from({ length: 18 }, (_, i) => ({ hour: i + 6 }))}
                                    getOptionLabel={h => h.hour.toString().padStart(2, '0')}
                                    getOptionValue={h => h.hour.toString()}
                                    value={{ hour: service.hour }}
                                    onChange={h => setService(x => ({ ...x, hour: h?.hour ?? 0 }))}
                                />
                                <Select className={'text-dark'}
                                    options={[{ minutes: 0 }, { minutes: 15 }, { minutes: 30 }, { minutes: 45 }, { minutes: 10 }, { minutes: 20 }, { minutes: 40 }, { minutes: 50 }]}
                                    getOptionLabel={m => m.minutes.toString().padStart(2, '0')}
                                    getOptionValue={m => m.minutes.toString()}
                                    value={{ minutes: service.minutes }}
                                    onChange={m => setService(x => ({ ...x, minutes: m?.minutes ?? 0 }))}
                                />
                            </div>
                            <h5 className={'mt-3'}>
                                Tipo de Servicio
                            </h5>
                            <select className={'form-select'}
                                value={service.typeOfPreaching ?? ''}
                                onChange={e => {
                                    const v = e.target.value as typePublicMinistryTypeOfPreaching;
                                    if (!typesOfPreaching.map(tp => tp.type).includes(v)) {
                                        return;
                                    }
                                    setService(x => ({ ...x, typeOfPreaching: v }))
                                }}
                            >
                                <option value='' disabled>
                                    Seleccionar Tipo de Servicio
                                </option>
                                {typesOfPreaching.map(tp =>
                                    <option key={tp.type} value={tp.type}>
                                        {tp.label}
                                    </option>
                                )}
                            </select>
                        </div>
                        <div className={'col-lg-3'}>
                            <h5>
                                Capitanes
                            </h5>
                            <div>
                                <Select className={'text-dark mb-3'}
                                    options={users.filter(u => u.id !== service.captain2?.id)}
                                    getOptionLabel={u => `${u.name} ${u.lastName}`}
                                    getOptionValue={u => u.id.toString()}
                                    placeholder={'Capitán Titular'}
                                    value={service.captain1}
                                    onChange={u => {
                                        setService(x => ({
                                            ...x,
                                            captain1: u
                                        }));
                                    }}
                                    isClearable
                                />
                                <Select className={'text-dark'}
                                    options={users.filter(u => u.id !== service.captain1?.id)}
                                    getOptionLabel={u => `${u.name} ${u.lastName}`}
                                    getOptionValue={u => u.id.toString()}
                                    placeholder={'Capitán Suplente'}
                                    value={service.captain2}
                                    onChange={u => {
                                        setService(x => ({
                                            ...x,
                                            captain2: u
                                        }));
                                    }}
                                    isClearable
                                />
                            </div>
                            {[2, 4].includes(service.typeOfRegularity) &&
                                <div className={'mb-3'}>
                                    <h5 className={'mt-3'}>
                                        GRUPOS
                                    </h5>
                                    <div className={'ps-1 mt-1'}>
                                        {Array.from({ length: config.numberOfGroups }, (_, i) => i + 1).map(g =>
                                            <div key={g} className={'d-flex align-items-center pointer mt-1'}
                                                onClick={() => {
                                                    if (!service.groups) {
                                                        return;
                                                    }
                                                    setService(x => ({
                                                        ...x,
                                                        groups: x.groups!.includes(g) ?
                                                            x.groups!.filter(_g => _g !== g)
                                                            :
                                                            [...x.groups!, g].sort((g1, g2) => g1 - g2)
                                                    }));
                                                }}
                                            >
                                                <input type={'checkbox'}
                                                    className={'form-check-input checkbox-md checkbox-red me-2'}
                                                    checked={service.groups.includes(g)}
                                                    onChange={() => {}}
                                                />
                                                <span className={'fw-bold'}>
                                                    Grupo {g}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                        </div>
                        <div className={'col-lg-3'}>
                            <h5>
                                Territorios
                            </h5>
                            <Select className={'text-dark'}
                                options={
                                    Array.from({ length: config.numberOfTerritories }, (_, i) => i + 1)
                                        .map(t => ({ territory: t }))
                                        .filter(t => !service.territories.includes(t.territory))
                                }
                                getOptionLabel={t => `Territorio ${t?.territory}`}
                                getOptionValue={t => t?.territory.toString() || ''}
                                placeholder={'Agregar Territorio'}
                                value={null}
                                onChange={t => {
                                    const v = t?.territory;
                                    if (!v) {
                                        return;
                                    }
                                    setService(x => ({
                                        ...x,
                                        territories: [...x.territories, v]
                                    }));
                                }}
                            />
                            <div className={'my-3'}>
                                Seleccionados: {!!service.territories.length ? <>{service.territories.join(', ')}</> : 'Ninguno'}
                                {repeatedTerritories.length > 1 &&
                                    <div className={'bg-yellow text-white fw-bold p-2 my-2'}>
                                        Los siguientes territorios ya están asignados a otras salidas: {repeatedTerritories.join(', ')}
                                    </div>
                                }
                                {repeatedTerritories.length === 1 &&
                                    <div className={'bg-yellow text-white fw-bold p-2 my-2'}>
                                        El territorio {repeatedTerritories[0]} ya está asignado a otra salida
                                    </div>
                                }
                            </div>
                            <div className={'mb-2'}>
                                <button className={'btn btn-secondary text-white w-100'}
                                    onClick={() => setService(x => ({
                                        ...x,
                                        territories: x.territories.sort((t1, t2) => t1 - t2)
                                    }))}
                                    disabled={service.territories.length < 2}
                                >
                                    Ordenar
                                </button>
                            </div>
                            <div>
                                <button className={'btn btn-general-red w-100'}
                                    onClick={() => setService(x => ({ ...x, territories: [] }))}
                                >
                                    Limpiar
                                </button>
                            </div>
                            <div className={'mt-5'}>
                                <p>
                                    *Datos obligatorios: día de la semana o fecha; al menos un grupo si la salida es de grupos.
                                </p>
                            </div>
                        </div>
                        <div className={'col-lg'}>
                            <h5>
                                Punto de Encuentro 1
                            </h5>
                            <input type={'text'}
                                className={'form-control'}
                                placeholder={'Dirección'}
                                value={service.address1}
                                onChange={e => {
                                    const v = e.target.value;
                                    if (v.length > 200) {
                                        return;
                                    }
                                    setService(x => ({
                                        ...x,
                                        address1: v
                                    }));
                                }}
                            />
                            <h5 className={'mt-3'}>
                                Punto de Encuentro 2
                            </h5>
                            <input type={'text'}
                                className={`form-control ${service.address2 && !service.address1 ? 'bg-danger' : ''}`}
                                placeholder={'Dirección'}
                                value={service.address2}
                                onChange={e => {
                                    const v = e.target.value;
                                    if (v.length > 200) {
                                        return;
                                    }
                                    setService(x => ({
                                        ...x,
                                        address2: v
                                    }));
                                }}
                            />
                            <h5 className={'mt-3'}>
                                Observaciones
                            </h5>
                            <input type={'text'}
                                className={'form-control'}
                                value={service.observation}
                                onChange={e => {
                                    const v = e.target.value;
                                    if (v.length > 200) {
                                        return;
                                    }
                                    setService(x => ({
                                        ...x,
                                        observation: v
                                    }));
                                }}
                            />
                        </div>
                    </div>
                    <div className={`card-footer row gap-4 text-center ${isDarkMode ? 'border-white' : ''} mx-2`}>
                        {isThereOverlap ?
                            <><FaExclamationCircle className={'text-warning mb-1 me-2'} />Hay superposición de salidas</>
                            :
                            <>
                            <button className={`btn btn-general-blue ${isEdit ? 'col-lg-3 offset-lg-3' : 'col w-100'} fw-bold py-2`}
                                onClick={() => acceptHandler(true)}
                                disabled={!isValid}
                            >
                                ACEPTAR
                            </button>
                            {isEdit ?
                                <button className={'btn btn-general-red col-lg-3 fw-bold py-2'}
                                    onClick={() => {
                                        deleteService?.(service);
                                        closeModal();
                                    }}
                                >
                                    ELIMINAR
                                </button>
                                :
                                <>
                                <button className={'btn btn-general-blue col w-100 fw-bold py-2'}
                                    onClick={() => acceptHandler(false)}
                                    disabled={!isValid || disabledAddOtherBtn}
                                >
                                    ACEPTAR Y AGREGAR OTRA
                                </button>
                                <button className={'btn btn-secondary col w-100 fw-bold py-2'}
                                    onClick={() => setService(getInit())}
                                >
                                    LIMPIAR
                                </button>
                                </>
                            }
                            </>
                        }
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
}
